import { useWidth } from "@/Shared/Hooks/useWidth";
import { Page } from "@/App/Page";
import { ShipmentMap } from "@/Shared/Maps/ShipmentMap";
import type { UUID } from "@/Shared/types";
import { useLocationsSaved } from "@/Shared/Locations/Hooks/useLocationsSaved";
import { useSavedCommodities } from "@/Features/SavedCommodities/Hooks/useSavedCommodities";
import { ShipmentLoadingError } from "@/Shared/Shipment/CustomComponents/ShipmentLoadingError";
import useQuery from "@/Hooks/useQuery";
import { useShipmentService } from "@/Services/ShipmentService";
import type { SavedCommodity } from "@freightsimple/generated-dashboard-openapi-client/models/SavedCommodity";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import { ResponseError } from "@freightsimple/generated-dashboard-openapi-client/runtime";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import BookShipmentNavigation from "@/Features/BookShipment/CustomComponents/BookShipmentNavigation";
import PageLoadingSpinner from "@/Shared/Components/CustomComponents/PageLoadingSpinner";
import { LoadFailed } from "@/Shared/Components/LoadFailed";
import { useBookShipmentAdditionalContentProvider } from "@/Features/BookShipment/Hooks/useBookShipmentAdditionalContentProvider";
import { useBookShipmentCompletedFlowItems } from "@/Features/BookShipment/Hooks/useBookShipmentCompletedFlowItems";
import { useBookShipmentFinishBookingFlow } from "@/Features/BookShipment/Hooks/useBookShipmentFinishBookingFlow";
import {
  loadFromShipment,
  resetShipment,
  setCurrentScreenIsNotDirty,
  setShipmentError,
  setShipmentLoading,
} from "@/Slices/bookShipmentSlice";
import { useBookShipment2Slice } from "@/Hooks/useBookShipment2Slice";
import { bookShipmentChromeForStep } from "@/Features/BookShipment/Helpers/bookShipmentStepChrome";
import { BookShipmentChromeLayout } from "@/App/BookShipmentChromeLayout";
import { bookShipmentRouterRoutes } from "@/Features/BookShipment/Internal/bookShipmentRouterRoutes";
import { useBookShipmentFlowNavigation } from "@/Features/BookShipment/Hooks/useBookShipmentFlowNavigation";

interface BookShipmentRouterWithSavedLocationsProps {
  savedLocations: Array<SavedLocation>;
  savedCommodities: Array<SavedCommodity>;
  defaultPickupLocationId: UUID | undefined;
  defaultDeliveryLocationId: UUID | undefined;
}

function BookShipmentRouterWithSavedLocations_(
  props: BookShipmentRouterWithSavedLocationsProps,
) {
  const {
    savedLocations,
    savedCommodities,
    defaultPickupLocationId,
    defaultDeliveryLocationId,
  } = props;
  const location = useLocation();
  // Measures the room the card has (signed-in beside the nav vs signed-out
  // differ at the same viewport). A ResizeObserver, not a CSS container
  // query — containment would re-base the fixed-position popovers inside.
  const [cardContainerRef, cardContainerWidth] = useWidth();
  const cardIsNarrow = cardContainerWidth > 0 && cardContainerWidth <= 640;

  // Basically try to match a url scheme like /book/:identifier
  const matches = location.pathname.match(/\/book\/([\w-]*)/);
  const pathIdentifier = matches?.length === 2 ? matches[1] : undefined;
  const navigate = useNavigate();
  const shipmentsService = useShipmentService();
  const query = useQuery();
  // query-string can return arrays/null for repeated params; ids are single values
  const shipmentIdParam = query["shipmentId"];
  const shipmentId =
    typeof shipmentIdParam === "string" ? shipmentIdParam : undefined;
  const shipmentDraftIdParam = query["shipmentDraftId"];
  const shipmentDraftId =
    typeof shipmentDraftIdParam === "string" ? shipmentDraftIdParam : undefined;

  const dispatch = useDispatch();

  const {
    shipment,
    loadingShipment,
    shipmentError,
    creditCardBrand,
    isModifying,
    isCurrentScreenDirty,
    completedFlowItems,
    makePickupLocationAFavourite,
    makeDeliveryLocationAFavourite,
    draft,
  } = useBookShipment2Slice();

  const finishBookingFlow = useBookShipmentFinishBookingFlow(shipment);

  const { additionalContentProvider } =
    useBookShipmentAdditionalContentProvider(
      shipment.pickupDate,
      shipment.pickupLocation,
      shipment.deliveryLocation,
      shipment.lineItems,
      creditCardBrand,
      shipment,
    );

  const { unlockedFlowItems } = useBookShipmentCompletedFlowItems(
    shipment,
    completedFlowItems,
  );

  const {
    onModify,
    onPrevious,
    onNavigate,
    dialogs: confirmDialogs,
  } = useBookShipmentFlowNavigation({
    shipment,
    savedLocations,
    shipmentId,
    isCurrentScreenDirty,
    finishBookingFlow,
  });

  async function getShipment() {
    // Only ever invoked when shipmentId is present (guarded below)
    if (shipmentId === undefined) {
      return;
    }
    dispatch(setShipmentError(undefined));
    dispatch(setShipmentLoading(true));
    try {
      const shipment = await shipmentsService.getPreBookingShipment(shipmentId);
      if (
        shipment.shipmentState === ShipmentState.BookingConfirmed ||
        shipment.shipmentState === ShipmentState.Cancelled ||
        shipment.shipmentState === ShipmentState.Lost ||
        shipment.shipmentState === ShipmentState.InTransit ||
        shipment.shipmentState === ShipmentState.Delivered
      ) {
        // We should NOT be in a booking flow
        navigate("/view-shipment?shipmentId=" + shipmentId);
        return;
      }
      dispatch(loadFromShipment({ shipment, savedLocations }));
    } catch (e) {
      console.error("Error loading shipment", { e });
      dispatch(
        setShipmentError({
          code: e instanceof ResponseError ? e.response.status : 500,
        }),
      );
    } finally {
      dispatch(setShipmentLoading(false));
    }
  }

  useEffect(
    function () {
      if (pathIdentifier) {
        if (shipmentId) {
          getShipment();
        } else {
          if (!isModifying) {
            dispatch(
              resetShipment({
                savedLocations,
                defaultPickupLocationId,
                defaultDeliveryLocationId,
              }),
            );
          }
        }
      }

      dispatch(setCurrentScreenIsNotDirty());
    },

    [pathIdentifier, shipmentId],
  );

  if (pathIdentifier === undefined) {
    return confirmDialogs;
  }

  const chrome = bookShipmentChromeForStep(pathIdentifier);

  const routes = bookShipmentRouterRoutes({
    shipment,
    loadingShipment,
    makePickupLocationAFavourite,
    makeDeliveryLocationAFavourite,
    draft,
    savedLocations,
    savedCommodities,
    shipmentId,
    shipmentDraftId,
    defaultPickupLocationId,
    defaultDeliveryLocationId,
    finishBookingFlow,
    dispatch,
    navigate,
    onModify,
    onPrevious,
    onNavigate,
  });

  if (loadingShipment) {
    return (
      <>
        {confirmDialogs}
        <PageLoadingSpinner />
      </>
    );
  }
  if (!loadingShipment && shipmentError !== undefined) {
    const code = shipmentError.code;
    return (
      <>
        {confirmDialogs}
        {code === 401 || code === 404 ? (
          <ShipmentLoadingError code={code} />
        ) : (
          <LoadFailed onRetry={getShipment} />
        )}
      </>
    );
  }

  return (
    <>
      {confirmDialogs}
      <BookShipmentChromeLayout
        chrome={chrome}
        cardContainerRef={cardContainerRef}
        cardIsNarrow={cardIsNarrow}
        map={
          <ShipmentMap
            pickup={shipment.pickupLocation}
            delivery={shipment.deliveryLocation}
            shipmentState={ShipmentState.Quoted}
          />
        }
        navigation={
          <BookShipmentNavigation
            currentFlowIdentifier={pathIdentifier}
            completedFlowItems={completedFlowItems}
            unlockedFlowItems={unlockedFlowItems}
            finishBookingFlow={finishBookingFlow}
            additionalContentProvider={additionalContentProvider}
            alwaysShowRequestQuoteFlow={true}
            onNavigate={onNavigate}
          />
        }
      >
        {routes.map(({ path, Component, identifier }) => (
          <Page key={path} path={path} id={identifier} exact={true}>
            {() => Component}
          </Page>
        ))}
      </BookShipmentChromeLayout>
    </>
  );
}

// This itself is just a wrapper to make sure the main router component is
// guaranteed to have saved locations by the time it mounts
function BookShipmentRouter() {
  const {
    savedLocations,
    loadingSavedLocations,
    savedLocationsLoadFailed,
    reloadSavedLocations,
    defaultPickupLocationId,
    defaultDeliveryLocationId,
  } = useLocationsSaved();

  const {
    savedCommodities,
    loadingSavedCommodities,
    savedCommoditiesLoadFailed,
    reloadSavedCommodities,
  } = useSavedCommodities();

  if (loadingSavedLocations || loadingSavedCommodities) {
    return <PageLoadingSpinner />;
  }

  // Every booking step is handed these lists, so none can render without them
  if (savedLocationsLoadFailed || savedCommoditiesLoadFailed) {
    return (
      <LoadFailed
        onRetry={() => {
          if (savedLocationsLoadFailed) reloadSavedLocations();
          if (savedCommoditiesLoadFailed) reloadSavedCommodities();
        }}
      />
    );
  }

  return (
    <BookShipmentRouterWithSavedLocations_
      savedLocations={savedLocations}
      savedCommodities={savedCommodities}
      defaultPickupLocationId={defaultPickupLocationId}
      defaultDeliveryLocationId={defaultDeliveryLocationId}
    />
  );
}

export default BookShipmentRouter;
